import { useMemo } from 'react';

import { Flex, Text, useSlotRecipe, VStack } from '@chakra-ui/react';

import { formatHelpers } from '@nocowanie/core';
import { Badge, Tooltip } from '@nocowanie/common-ui';

import { apiTypes } from '@app/models';

import { PriceProps } from './price.props';

export const Price = ({
    currency,
    price,
    basePrice,
    children,
    discounts,
    convertedCurrency,
    convertedPrice,
    convertedBasePrice,
    discountValue,
    variant,
    withFlexDates = false,
    priceWrapperProps,
    discountProps,
    priceFromLabel,
    ...rest
}: PriceProps) => {
    const recipe = useSlotRecipe({ key: 'price' });
    const styles = recipe({ variant });

    const discount = useMemo(() => {
        if (!basePrice || !price || basePrice === price) {
            return undefined;
        }
        return Math.round(discountValue ?? ((basePrice - price) / basePrice) * 100).toFixed(0);
    }, [basePrice, price, discountValue]);

    const renderDiscountInfo = useMemo(() => {
        const seen = new Set<string>();
        const uniqueDiscounts =
            discounts?.filter(discount => {
                if (seen.has(discount.discountInfo)) {
                    return false;
                }
                seen.add(discount.discountInfo);
                return true;
            }) || [];

        return uniqueDiscounts.map((discount: apiTypes.Discount) => (
            <Text key={discount?.id}>{discount?.discountInfo}</Text>
        ));
    }, [discounts]);

    const withForeignPrice = convertedCurrency && convertedCurrency !== currency;

    const renderBadge = discount ? (
        <Badge
            data-testid={'price-discount'}
            css={styles.discountBadge}
            variant={'solid'}
            labelProps={{
                fontSize: {
                    base: 'sm',
                    md: variant === 'accommodationTile' ? 'sm' : 'lg',
                },
            }}
            hideBelow={withFlexDates ? 'md' : undefined}
            {...discountProps}
        >
            {`-${discount}%`}
        </Badge>
    ) : null;

    return (
        <VStack data-testid={'price'} alignItems={'flex-end'} gap={0} {...rest}>
            {discount ? (
                discounts?.length ? (
                    <Tooltip
                        positioning={{
                            placement: 'top',
                        }}
                        showTriggerIcon={false}
                        extendedTriggerArea={{
                            additionalTrigger: renderBadge,
                            wrapperProps: {
                                cursor: 'pointer',
                            },
                        }}
                    >
                        {renderDiscountInfo}
                    </Tooltip>
                ) : (
                    renderBadge
                )
            ) : null}
            <Flex css={styles.priceValueWrapper} {...priceWrapperProps}>
                {discount ? (
                    <Text data-testid={'price-base'} as="del" css={styles.discountPrice}>
                        {basePrice && formatHelpers.price(basePrice, currency)}
                        {convertedBasePrice && withForeignPrice
                            ? ` (∼${formatHelpers.price(convertedBasePrice, convertedCurrency)})`
                            : null}
                    </Text>
                ) : null}
                <Text
                    data-testid={'price-final'}
                    as={'strong'}
                    css={styles.mainPrice}
                    color={discount ? 'brand.solid' : undefined}
                >
                    {priceFromLabel ? (
                        <Text fontSize={'xs'} fontWeight={'normal'} as={'span'} mr={1}>
                            {priceFromLabel}
                        </Text>
                    ) : null}
                    {formatHelpers.price(price, currency)}
                    {convertedPrice && withForeignPrice ? (
                        <Text as={'span'} css={styles.formattedPrice}>
                            (∼{formatHelpers.price(convertedPrice, convertedCurrency)})
                        </Text>
                    ) : null}
                </Text>
            </Flex>
            {children}
        </VStack>
    );
};
